import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { Company } from 'src/app/core/interfaces/company';
import { GuideNovelty } from 'src/app/core/interfaces/guideNovelty';
import { Permission } from 'src/app/core/resources/permission';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { CompaniesService } from '../list-companies.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AccountService } from 'src/app/modules/account/account.service';
import { AuthService } from 'src/app/core/services/authentication.service';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import { GuideNoveltyFormComponent } from '../guide-novelty-form/guide-novelty-form.component';
import { FormControl } from '@angular/forms';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { Utils } from 'src/app/core/resources/utils';
import { SettingId } from 'src/app/core/interfaces/saas-config';

@Component({
  selector: 'app-guide-novelties',
  templateUrl: './guide-novelties.component.html',
  styleUrls: ['./guide-novelties.component.scss']
})
export class GuideNoveltiesComponent implements OnInit, OnChanges {

  permission = Permission;
  @Input() company: Company;
  noveltyList: GuideNovelty[] = [];
  noveltyTypes: string[] = ["Bloqueante", "No Bloqueante", "Todas"];
  noveltyFilter: FormControl = new FormControl("Todas");
  useEvidenceToGuidesControl: FormControl = new FormControl(false);

  constructor(
    private snackBarService: SnackBarService,
    private permissionRole: PermissionRole,
    private spinner: NgxSpinnerService,
    private accountService: AccountService,
    private authService: AuthService,
    private listCompaniesService: CompaniesService,
    public dialog: MatDialog,
    private utils: Utils,
  ) { }

  ngOnInit() {
    this.getNoveltyList();
    this.initializeUseEvidenceToGuidesControl();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.company && !changes.company.firstChange && changes.company.previousValue !== changes.company.currentValue) this.initializeUseEvidenceToGuidesControl();
  }

  private initializeUseEvidenceToGuidesControl() {
    const value = !this.utils.isDefined(this.company.useEvidenceToGuides) ? true : this.company.useEvidenceToGuides;
    this.useEvidenceToGuidesControl.setValue(value, { emitEvent: false });
  }

  private getNoveltyList() {
    this.spinner.show();
    this.listCompaniesService.getGuideNovelties(this.company.companyId).subscribe(
      (novelties: GuideNovelty[]) => {
        this.noveltyList = novelties && novelties.length ? novelties : [];
        this.spinner.hide();
      }, (error) => {
        this.noveltyList = [];
        this.spinner.hide();
      });
  }


  get filteredNovelties(): GuideNovelty[] {
    if (this.noveltyFilter.value === "Todas") return this.noveltyList;
    return this.noveltyList.filter(novelty => {
      const isBlocked = this.noveltyFilter.value === "Bloqueante";
      const isNotBlocked = this.noveltyFilter.value === "No Bloqueante";
      return novelty && (isBlocked && novelty.type === 'Anomaly') || (isNotBlocked && novelty.type === 'Normal');
    });
  }

  createNovelty() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: 'Crear novedad',
      isPrincipalCompany: this.isPrincipalCompany,
      noveltyList: this.noveltyList,
      confirmBtn: 'Crear',
      companyId: this.company.companyId,
    };
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
    dialogConfig.width = ModalEnum.EXTRA_SMALL_WIDTH;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.autoFocus = false;
    const dialogRef = this.dialog.open(GuideNoveltyFormComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.state)
        this.getNoveltyList();
    });
  }

  editNovelty(novelty: GuideNovelty) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      novelty,
      isPrincipalCompany: this.isPrincipalCompany,
      companyId: this.company.companyId,
      noveltyList: this.noveltyList,
      title: 'Editar novedad',
      confirmBtn: 'Guardar'
    };
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
    dialogConfig.width = ModalEnum.EXTRA_SMALL_WIDTH;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.autoFocus = false;
    const dialogRef = this.dialog.open(GuideNoveltyFormComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.state)
        this.getNoveltyList();
    });
  }

  get canEditNovelty(): boolean {
    return this.permissionRole.hasPermission(this.permission.guide.module, this.permission.guide.editGuideNovelties);
  }

  get canEditUseEvidenceToGuides(): boolean {
    return this.permissionRole.hasPermission(this.permission.guide.module, this.permission.guide.editUseEvidenceToGuides);
  }

  get isUseEvidenceToGuidesEnabledInSaas(): boolean {
    return this.authService && this.authService.hasCompanySettingEnabled(SettingId.USE_EVIDENCE_TO_GUIDES);
  }

  get isPrincipalCompany(): boolean {
    const listCompanies = this.accountService.listRegisterCompanies();
    return !!(listCompanies.length && this.company && this.company.companyId &&
      listCompanies.some(company => company.nit === this.company.companyId) && this.authService &&
      this.authService.getCompany() && this.authService.getCompany().companyId === this.company.companyId);
  }

  get useEvidenceToGuides(): boolean {
    return this.useEvidenceToGuidesControl.value;
  }

  async setUseEvidenceToGuides(value: boolean) {
    const previousValue = this.useEvidenceToGuidesControl.value;
    this.spinner.show();
    let success = false;
    try {
      const serviceResponse = await this.listCompaniesService.updateUseEvidenceToGuides(
        this.company.companyId,
        value
      ).toPromise();
      success = !!serviceResponse;
    } catch (error) {
      this.useEvidenceToGuidesControl.setValue(previousValue, { emitEvent: false });
      console.error('Error al actualizar useEvidenceToGuides:', error);
    } finally {
      this.spinner.hide();
    }
    if (success) {
      if (this.company) this.company.useEvidenceToGuides = value;
      this.snackBarService.openSnackBar('Configuración de evidencias actualizada correctamente', undefined, 'success');
    } else this.snackBarService.openSnackBar('Ocurrió un error al actualizar la configuración de evidencias', undefined, 'error');
  }

}
